// src/lib/passwordStrength.js
// Heurística simple de fortaleza de contraseña. Devuelve un score 0-5,
// label legible, color de marca y sugerencias específicas para que el
// usuario sepa qué mejorar.

const NIVELES = [
  { score: 0, label: 'Muy débil',  color: '#b94545', pct: 8  },
  { score: 1, label: 'Débil',      color: '#d97706', pct: 25 },
  { score: 2, label: 'Aceptable',  color: '#d97706', pct: 50 },
  { score: 3, label: 'Buena',      color: '#3a6e10', pct: 70 },
  { score: 4, label: 'Fuerte',     color: '#3a6e10', pct: 88 },
  { score: 5, label: 'Muy fuerte', color: '#27500a', pct: 100 },
];

// Patrones típicos que reducen fortaleza
const COMUNES = /^(123|abc|qwer|password|contraseña|admin|vera|2026|2025|0000|1111)/i;
const SECUENCIA_NUMEROS = /(0123|1234|2345|3456|4567|5678|6789)/;

/**
 * Evalúa una contraseña y devuelve { score 0-5, label, color, pct, sugerencias }.
 * Las sugerencias son cosas concretas que el usuario puede hacer para mejorar.
 */
export function evaluarPassword(pwd) {
  const password = String(pwd || '');
  const sugerencias = [];

  if (password.length === 0) {
    return { ...NIVELES[0], score: -1, label: '', pct: 0, sugerencias: [] };
  }

  let score = 0;
  const tieneMinus = /[a-z]/.test(password);
  const tieneMayus = /[A-Z]/.test(password);
  const tieneNum   = /\d/.test(password);
  const tieneSym   = /[^a-zA-Z0-9]/.test(password);

  // Longitud — el factor que más pesa en passwords reales
  if (password.length >= 8)  score += 1;
  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;

  // Variedad de caracteres
  if (tieneMinus && tieneMayus) score += 1;
  if (tieneNum) score += 1;
  if (tieneSym) score += 1;

  // Penalización por patrones predecibles
  if (COMUNES.test(password)) score = Math.max(0, score - 2);
  if (SECUENCIA_NUMEROS.test(password)) score = Math.max(0, score - 1);

  // Si toda la contraseña es repetición de pocos caracteres distintos
  const uniques = new Set(password).size;
  if (uniques < password.length / 2 && password.length > 4) {
    score = Math.max(0, score - 1);
  }

  score = Math.min(5, Math.max(0, score));

  // Sugerencias específicas (priorizadas)
  if (password.length < 8)         sugerencias.push('Usa al menos 8 caracteres');
  else if (password.length < 12)   sugerencias.push('Más larga = más segura (12+ caracteres)');
  if (!tieneMayus || !tieneMinus)  sugerencias.push('Combina mayúsculas y minúsculas');
  if (!tieneNum)                   sugerencias.push('Agrega un número');
  if (!tieneSym)                   sugerencias.push('Agrega un símbolo (!@#$...)');
  if (COMUNES.test(password))      sugerencias.push('Evita palabras comunes o predecibles');
  if (SECUENCIA_NUMEROS.test(password)) sugerencias.push('Evita secuencias numéricas (1234, 5678…)');

  return { ...NIVELES[score], sugerencias: sugerencias.slice(0, 3) };
}

/**
 * Score mínimo aceptable para forzar al usuario a no usar contraseñas
 * triviales. 1 = "Débil" (8 chars cualquier cosa). 2 = "Aceptable".
 * Mantenemos en 1 para no frustrar pero descomentar el default a 2 cuando
 * llegue el primer cliente real con datos sensibles.
 */
export const SCORE_MINIMO = 1;
